import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import type { Size } from '@view-model/models/common/basic';
import { fileExtensionIcon } from './fileExtensionIcon';

const TooltipDelayMs = 1000;
const IconColor = '#A0AEC0';

type Props = {
  filename: string;
  size: Size;
  isSelected: boolean;
  isUploading?: boolean;
};

export const FileCard: React.FC<Props> = ({ filename, size, isSelected, isUploading = false }: Props) => {
  const { t } = useTranslation();
  const filenameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const iconSize = Math.round(size.height * 0.3);
  const fontSize = Math.round(size.height * 0.09);

  useLayoutEffect(() => {
    const element = filenameRef.current;
    if (element) setIsTruncated(element.scrollHeight > element.clientHeight);
  }, [filename, fontSize]);

  return (
    <g opacity={isUploading ? 0.6 : 1}>
      <rect
        width={size.width}
        height={size.height}
        rx={4}
        fill="#ffffff"
        stroke={isSelected ? '#3b82f6' : '#d1d5db'}
        strokeWidth={isSelected ? 2 : 1}
      />
      <foreignObject width={size.width} height={size.height}>
        <DarkTooltip
          tooltip={isTruncated ? filename : null}
          placement="bottom"
          delayMs={TooltipDelayMs}
          className="flex size-full flex-col items-center justify-center gap-3 p-4 text-gray-700"
        >
          <FontAwesomeIcon icon={fileExtensionIcon(filename)} style={{ fontSize: iconSize, color: IconColor }} />
          <span
            ref={filenameRef}
            className="line-clamp-2 w-full break-all text-center leading-tight"
            style={{ fontSize }}
          >
            {filename}
          </span>
          {isUploading && (
            <span className="text-gray-500" style={{ fontSize: fontSize * 0.8 }}>
              {t('canvas.fileContainer.status.uploading')}
            </span>
          )}
        </DarkTooltip>
      </foreignObject>
    </g>
  );
};
